import {
  type StudentGrade,
  type ViewContext,
  gradeLevel,
  pointsAverage,
  shortDate,
  termFor,
  todayIso,
  useStudentRecord,
  useTerms,
} from '@shkola/api';
import { type CutSticker, subjectLook } from '@shkola/design';
import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sticker } from '../design/kit';
import { gradeShort } from '../lib/labels';

type StudentContext = Extract<ViewContext, { kind: 'student' }>;

const FEEDBACK_STICKERS: Record<string, CutSticker> = {
  star: 'star',
  progress: 'bar-chart',
  effort: 'sports-medal',
  idea: 'light-bulb',
  help: 'red-heart',
};

/** thematic and term grades stand out on soft lavender; the rest are grey */
const FINAL = new Set<StudentGrade['kind']>(['thematic', 'semester', 'year']);

function Chip({ grade }: { grade: StudentGrade }) {
  const { t } = useTranslation();
  const value = gradeShort(t, grade.value, grade.special);
  return (
    <span
      title={`${shortDate(grade.date)} · ${t(`journal.kinds.${grade.kind}`)}${grade.topic ? ` · ${grade.topic}` : ''}`}
      className={`inline-flex h-9 min-w-9 items-center justify-center rounded-xl px-2 font-display text-[15px] font-bold ${FINAL.has(grade.kind) ? 'bg-accent-soft' : 'bg-fill'} ${gradeLevel(grade.value) === 0 ? 'text-red-pen' : 'text-ink'}`}
    >
      {value}
    </span>
  );
}

/** How many of each grade: twelve thin bars, like the ticks of a ruler. */
function Spread({ grades }: { grades: StudentGrade[] }) {
  const counts = Array.from({ length: 12 }, () => 0);
  for (const g of grades) {
    if (g.value?.scale === 'points12')
      counts[g.value.points - 1] = (counts[g.value.points - 1] ?? 0) + 1;
  }
  const max = Math.max(1, ...counts);
  return (
    <div className="mt-5 grid grid-cols-12 gap-1.5">
      {counts.map((n, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <div className="flex h-14 w-full items-end">
            <span
              className={`block w-full rounded-[4px] ${n ? 'bg-ink' : 'bg-fill'}`}
              style={{ height: `${Math.max(6, (n / max) * 100)}%` }}
            />
          </div>
          <span className="text-[11px] font-semibold text-faint">{i + 1}</span>
        </div>
      ))}
    </div>
  );
}

/** One subject as a row of the list: its sticker, the grades in order, the average; opens up. */
function SubjectRow({
  subject,
  grades,
  missed,
  index,
}: {
  subject: string;
  grades: StudentGrade[];
  missed: number;
  index: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const look = subjectLook(subject);
  const average = pointsAverage(grades);
  return (
    <div
      className="motion-rise border-t border-line first:border-t-0"
      style={{ '--delay': `${index * 0.03}s` } as CSSProperties}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="group flex w-full items-center gap-4 rounded-[20px] px-4 py-3.5 text-left transition-colors hover:bg-fill/60"
      >
        <Sticker
          name={look.sticker}
          size={40}
          rotate={-8}
          className="shrink-0 transition-transform duration-500 ease-spring group-hover:rotate-6"
        />
        <span className="w-52 shrink-0">
          <span className="block truncate font-display text-[17px] leading-5 font-bold text-ink">
            {subject}
          </span>
          <span className="text-[13px] text-muted">
            {missed > 0 ? t('grades.absencesCount', { count: missed }) : ' '}
          </span>
        </span>
        <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
          {grades.map((g) => (
            <Chip key={g.id} grade={g} />
          ))}
        </span>
        <span className="w-14 shrink-0 text-right font-display text-[26px] font-bold tracking-tight text-ink">
          {average !== null ? String(average).replace('.', ',') : '—'}
        </span>
      </button>
      {open && (
        <div className="motion-rise space-y-2 px-4 pb-4 pl-[76px]">
          {[...grades].reverse().map((g) => (
            <p key={g.id} className="flex items-center gap-3 text-[13px] text-ink-soft">
              <Chip grade={g} />
              {shortDate(g.date)} · {t(`journal.kinds.${g.kind}`)}
              {g.topic ? ` · ${g.topic}` : g.title ? ` · ${g.title}` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The student's grades in three blocks: the average on squared paper with how the grades spread,
 * the teachers' words on a ruled page, and every subject as a row of one white list.
 */
export function StudentGrades({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const terms = useTerms(ctx.schoolId);
  const [termId, setTermId] = useState<string>();
  const term =
    terms.data?.find((x) => x.id === termId) ?? (terms.data && termFor(terms.data, todayIso()));
  const record = useStudentRecord(ctx.child.memberId, term?.id);
  const grades = record.data?.grades ?? [];
  const absences = record.data?.absences ?? [];
  const formative = record.data?.formative ?? [];
  const average = pointsAverage(grades);
  const bySubject = new Map<string, StudentGrade[]>();
  for (const g of grades) bySubject.set(g.subject, [...(bySubject.get(g.subject) ?? []), g]);
  const subjects = [...bySubject.keys()].sort((a, b) => a.localeCompare(b, 'uk'));

  return (
    <div className="space-y-2">
      <header className="flex flex-wrap items-end justify-between gap-4 rounded-[28px] bg-white px-7 py-6">
        <div>
          {term && (
            <p className="-rotate-2 font-hand text-2xl font-bold text-ink-soft">
              {t('journal.term', { n: term.number })}
            </p>
          )}
          <h1 className="font-display text-5xl font-bold tracking-tight text-ink">
            {t('grades.title')}
          </h1>
        </div>
        {terms.data && terms.data.length > 1 && (
          <div className="flex rounded-full bg-fill p-1">
            {terms.data.map((x) => (
              <button
                key={x.id}
                type="button"
                aria-pressed={x.id === term?.id}
                onClick={() => setTermId(x.id)}
                className={`rounded-full px-5 py-2 text-[15px] font-semibold transition ${x.id === term?.id ? 'bg-ink text-white' : 'text-muted'}`}
              >
                {t('journal.term', { n: x.number })}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="grid items-start gap-2 lg:grid-cols-[380px_minmax(0,1fr)]">
        <div className="space-y-2">
          <section className="paper-grid relative overflow-hidden rounded-[28px] p-6">
            <Sticker
              name="glowing-star"
              size={84}
              rotate={14}
              className="absolute -top-2 -right-1"
            />
            <p className="text-[15px] font-semibold text-muted first-letter:uppercase">
              {t('student.stickers.average')}
            </p>
            <p className="font-display text-[88px] leading-none font-bold tracking-[-0.04em] text-ink">
              {average !== null ? String(average).replace('.', ',') : '—'}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-white px-3.5 py-1.5 text-sm font-semibold text-ink">
                {t('student.grades.count', { count: grades.length })}
              </span>
              <span className="rounded-full bg-white px-3.5 py-1.5 text-sm font-semibold text-ink">
                {t('grades.absencesCount', { count: absences.length })}
              </span>
            </div>
            <Spread grades={grades} />
          </section>

          {formative.length > 0 && (
            <section className="paper-lined relative overflow-hidden rounded-[28px] pb-4">
              <span className="absolute inset-y-0 left-[64px] w-[1.5px] bg-paper-margin" />
              <h2 className="flex h-[56px] items-end pb-2 pl-[80px] font-display text-lg font-bold text-ink">
                {t('grades.formative')}
              </h2>
              {formative.slice(0, 5).map((f) => (
                <article key={f.id} className="relative py-1.5 pr-5 pl-[80px]">
                  <Sticker
                    name={(f.sticker && FEEDBACK_STICKERS[f.sticker]) || 'speech-balloon'}
                    size={36}
                    rotate={-8}
                    className="absolute top-2 left-3.5"
                  />
                  <p className="text-[13px] leading-7 font-semibold text-muted">
                    {f.subject} · {shortDate(f.date)}
                  </p>
                  {f.comment && <p className="text-[15px] leading-7 text-ink">{f.comment}</p>}
                </article>
              ))}
            </section>
          )}
        </div>

        <section className="rounded-[28px] bg-white p-2">
          <h2 className="px-4 pt-4 pb-2 font-display text-[22px] font-bold tracking-tight text-ink">
            {t('grades.bySubject')}
          </h2>
          {subjects.length === 0 && !record.isPending && (
            <p className="px-4 pb-4 text-muted">{t('grades.noGrades')}</p>
          )}
          {subjects.map((s, i) => (
            <SubjectRow
              key={s}
              index={i}
              subject={s}
              grades={[...(bySubject.get(s) ?? [])].sort((a, b) => a.date.localeCompare(b.date))}
              missed={absences.filter((a) => a.subject === s).length}
            />
          ))}
        </section>
      </div>
    </div>
  );
}
